import React, { Suspense } from "react";
import { BrowserRouter } from "react-router-dom";
import { Routes } from "react-router-dom";
import { Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import AppLayout from "components/ui/AppLayout";
import NotFound from "pages/NotFound";

// CONSISTENT LAZY LOADING - Convert all imports to lazy loading to fix the dynamic import error
const AdminPanel = React.lazy(() => import('./pages/admin-panel'));
const TenantSelection = React.lazy(() => import('./pages/tenant-selection'));
const HomeDashboard = React.lazy(() => import('./pages/home-dashboard'));
const LoginPage = React.lazy(() => import('./pages/login'));
const Marketplace = React.lazy(() => import('./pages/marketplace'));
const PlaybookDetail = React.lazy(() => import('./pages/playbook-detail'));
const FinancialsDashboard = React.lazy(() => import('./pages/financials-dashboard'));
const AIInsights = React.lazy(() => import('./pages/ai-insights'));
const MarketingLandingPage = React.lazy(() => import('./pages/marketing-landing-page'));

// Capital Pack Components - Portfolio Intelligence Platform
const CapitalPortfolioDashboard = React.lazy(() => import('./pages/capital-portfolio-dashboard'));
const CapitalSectorSelection = React.lazy(() => import('./pages/capital-sector-selection'));
const CapitalCompanyDetail = React.lazy(() => import('./pages/capital-company-detail'));
const CapitalEarlyWarningCentre = React.lazy(() => import('./pages/capital-early-warning-centre'));
const CapitalScenarioSimulator = React.lazy(() => import('./pages/capital-scenario-simulator'));
const CapitalPortfolioCopilot = React.lazy(() => import('./pages/capital-portfolio-copilot'));
const CapitalPredictiveCovenantMonitor = React.lazy(() => import('./pages/capital-predictive-covenant-monitor'));
const CapitalLPPackAutoWriter = React.lazy(() => import('./pages/capital-lp-pack-auto-writer'));
const CapitalIntegrationsManagement = React.lazy(() => import('./pages/capital-integrations-management'));
const CapitalSettingsConfiguration = React.lazy(() => import('./pages/capital-settings-configuration'));
const CapitalMenu = React.lazy(() => import('./pages/capital-menu'));
const MSPSectorPerformance = React.lazy(() => import('./pages/msp-sector-performance'));

// Pack-specific lazy loaded components
const PackSelectionOnboarding = React.lazy(() => import('./pages/pack-selection-onboarding'));
const CrecheToday = React.lazy(() => import('./pages/creche/today'));
const CrecheAttendance = React.lazy(() => import('./pages/creche/attendance'));
const CrecheFunding = React.lazy(() => import('./pages/creche/funding'));
const CrecheBilling = React.lazy(() => import('./pages/creche/billing'));
const CrecheRota = React.lazy(() => import('./pages/creche/rota'));
const CrecheCompliance = React.lazy(() => import('./pages/creche/compliance'));
const CrecheGroup = React.lazy(() => import('./pages/creche/group'));
const CentresOverview = React.lazy(() => import('./pages/centres-overview'));
const CentreDetail = React.lazy(() => import('./pages/centre-detail'));

// Convert all remaining direct imports to lazy loading for consistency
const DataHubProfiles = React.lazy(() => import('./pages/data-hub-profiles'));
const DataIntelligenceSuiteHub = React.lazy(() => import('./pages/data-intelligence-suite-hub'));
const EntityResolutionQueue = React.lazy(() => import('./pages/entity-resolution-queue'));
const ImportDrawer = React.lazy(() => import('./pages/import-drawer'));
const Network = React.lazy(() => import('./pages/network'));
const ReputationLocations = React.lazy(() => import('./pages/reputation-locations'));
const ReputationLocationDetail = React.lazy(() => import('./pages/reputation-location-detail'));
const ReputationSettings = React.lazy(() => import('./pages/reputation-settings'));
const ReputationHealthAudit = React.lazy(() => import('./pages/reputation-health-audit'));
const RebatesManagement = React.lazy(() => import('./pages/rebates-management'));
const LicensesManagement = React.lazy(() => import('./pages/licenses-management'));
const CloudCostManagement = React.lazy(() => import('./pages/cloud-cost-management'));
const Companies = React.lazy(() => import('./pages/companies'));
const CompanyDetail = React.lazy(() => import('./pages/company-detail'));
const PracticesManagement = React.lazy(() => import('./pages/practices-management'));
const PracticeDetails = React.lazy(() => import('./pages/practice-details'));
const PlaybooksManagement = React.lazy(() => import('./pages/playbooks-management'));
const IntegrationsManagement = React.lazy(() => import('./pages/integrations-management'));
const SmartOrchestrator = React.lazy(() => import('./pages/smart-orchestrator'));
const SuppliesVendors = React.lazy(() => import('./pages/supplies-vendors'));
const Reports = React.lazy(() => import('./pages/reports'));
const PayerFeeSchedules = React.lazy(() => import('./pages/payer-fee-schedules'));
const MarketIntelligence = React.lazy(() => import('./pages/market-intelligence'));

// Capital Pack additional pages
const FinancialOptimizationHub = React.lazy(() => import('./pages/financial-optimization-hub'));
const SupplyChainManagementHub = React.lazy(() => import('./pages/supply-chain-management-hub'));
const ExpansionROIModeling = React.lazy(() => import('./pages/expansion-roi-modeling'));
const WhatIfScenarioPlanning = React.lazy(() => import('./pages/what-if-scenario-planning'));
const PredictiveAnalyticsForecasting = React.lazy(() => import('./pages/predictive-analytics-forecasting'));
const MarketPenetrationAnalysis = React.lazy(() => import('./pages/market-penetration-analysis'));
const QualityControlSystems = React.lazy(() => import('./pages/quality-control-systems'));
const DataSynchronizationManager = React.lazy(() => import('./pages/data-synchronization-manager'));
const CompetitiveIntelligenceDashboard = React.lazy(() => import('./pages/competitive-intelligence-dashboard'));
const StrategicPlanningHub = React.lazy(() => import('./pages/strategic-planning-hub'));
const SiteSelectionFeasibilityAnalysis = React.lazy(() => import('./pages/site-selection-feasibility-analysis'));
const AcquisitionTargetScreening = React.lazy(() => import('./pages/acquisition-target-screening'));
const ValuationAnalysis = React.lazy(() => import('./pages/valuation-analysis'));
const CorrelationAnalysis = React.lazy(() => import('./pages/correlation-analysis'));
const ExecutiveDashboardBuilder = React.lazy(() => import('./pages/executive-dashboard-builder'));
const RevenueManagementTools = React.lazy(() => import('./pages/revenue-management-tools'));
const BenchmarkingTools = React.lazy(() => import('./pages/benchmarking-tools'));
const DynamicPricingEngine = React.lazy(() => import('./pages/dynamic-pricing-engine'));
const CashFlowForecasting = React.lazy(() => import('./pages/cash-flow-forecasting'));
const PortfolioAnalyticsOverview = React.lazy(() => import('./pages/portfolio-analytics-overview'));
const TrendAnalysis = React.lazy(() => import('./pages/trend-analysis'));
const AutomatedReordering = React.lazy(() => import('./pages/automated-reordering'));
const SupplierPerformanceTracking = React.lazy(() => import('./pages/supplier-performance-tracking'));

// Enhanced loading fallback component with better error boundaries
const PageLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <p className="text-gray-600 text-sm">Loading application...</p>
    </div>
  </div>
);

// Layout loading fallback with improved UI
const LayoutLoadingFallback = () => (
  <div className="flex items-center justify-center h-64">
    <div className="flex flex-col items-center space-y-3">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <p className="text-gray-600 text-sm">Loading page...</p>
    </div>
  </div>
);

// Error fallback component for failed lazy loads with retry mechanism
const LazyLoadErrorFallback = ({ error, resetErrorBoundary }) => {
  const handleRetry = () => {
    // Clear Vite's module cache before retrying
    if (window?.location) {
      // Force a hard reload to clear all module caches
      window.location.href = window.location?.href?.split('?')?.[0] + '?_reload=' + Date.now();
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center p-8 max-w-md">
        <div className="text-red-500 text-6xl mb-4">⚠️</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load page</h2>
        <p className="text-gray-600 mb-2">There was an error loading this page module.</p>
        <p className="text-sm text-gray-500 mb-6">This often happens due to build cache issues. A page reload should fix it.</p>
        <div className="flex gap-3 justify-center">
          <button 
            onClick={handleRetry}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            Reload Page
          </button>
          <button 
            onClick={resetErrorBoundary}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            Try Again
          </button>
        </div>
        {error && (
          <details className="mt-4 text-left">
            <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
              Error Details
            </summary>
            <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-auto max-h-32">
              {error?.message || 'Unknown error'}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

// Move mockNotifications outside component to prevent recreation on every render
const MOCK_NOTIFICATIONS = [
  {
    id: 1,
    title: 'System Status',
    message: 'All systems operational',
    time: '5 min ago',
    read: false
  }
];

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary fallback={LazyLoadErrorFallback}>
        <ScrollToTop />
        <Routes>
          {/* Full-screen pages without layout */}
          <Route path="/" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <MarketingLandingPage />
            </Suspense>
          } />
          <Route path="/login" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <LoginPage />
            </Suspense>
          } />
          <Route path="/tenant-selection" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <TenantSelection />
            </Suspense>
          } />
          <Route path="/pack-selection-onboarding" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <PackSelectionOnboarding />
            </Suspense>
          } />
          
          {/* CAPITAL PACK ROUTES */}
          <Route path="/capital-portfolio-dashboard" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CapitalPortfolioDashboard />
              </Suspense>
            </AppLayout>
          } />
          
          {/* NEW MSP SECTOR PERFORMANCE ROUTE */}
          <Route path="/msp-sector-performance" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <MSPSectorPerformance />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/capital-sector-selection" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CapitalSectorSelection />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/capital-company-detail" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CapitalCompanyDetail />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/capital-early-warning-centre" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CapitalEarlyWarningCentre />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/capital-scenario-simulator" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CapitalScenarioSimulator />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/capital-portfolio-copilot" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CapitalPortfolioCopilot />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/capital-lp-pack-auto-writer" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CapitalLPPackAutoWriter />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/capital-predictive-covenant-monitor" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CapitalPredictiveCovenantMonitor />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/capital-integrations-management" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CapitalIntegrationsManagement />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/capital-settings-configuration" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CapitalSettingsConfiguration />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/capital-menu" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CapitalMenu />
              </Suspense>
            </AppLayout>
          } />
          
          {/* ALL CAPITAL PACK EXTENDED ROUTES */}
          <Route path="/financial-optimization-hub" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <FinancialOptimizationHub />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/supply-chain-management-hub" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <SupplyChainManagementHub />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/expansion-roi-modeling" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <ExpansionROIModeling />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/what-if-scenario-planning" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <WhatIfScenarioPlanning />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/predictive-analytics-forecasting" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <PredictiveAnalyticsForecasting />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/market-penetration-analysis" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <MarketPenetrationAnalysis />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/quality-control-systems" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <QualityControlSystems />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/data-synchronization-manager" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <DataSynchronizationManager />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/competitive-intelligence-dashboard" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CompetitiveIntelligenceDashboard />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/strategic-planning-hub" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <StrategicPlanningHub />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/site-selection-feasibility-analysis" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <SiteSelectionFeasibilityAnalysis />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/acquisition-target-screening" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <AcquisitionTargetScreening />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/valuation-analysis" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <ValuationAnalysis />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/correlation-analysis" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CorrelationAnalysis />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/executive-dashboard-builder" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <ExecutiveDashboardBuilder />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/revenue-management-tools" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <RevenueManagementTools />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/benchmarking-tools" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <BenchmarkingTools />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/dynamic-pricing-engine" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <DynamicPricingEngine />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/cash-flow-forecasting" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CashFlowForecasting />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/portfolio-analytics-overview" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <PortfolioAnalyticsOverview />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/trend-analysis" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <TrendAnalysis />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/automated-reordering" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <AutomatedReordering />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/supplier-performance-tracking" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <SupplierPerformanceTracking />
              </Suspense>
            </AppLayout>
          } />
          
          {/* CRECHE ROUTES */}
          <Route path="/creche/today" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CrecheToday />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/centres-overview" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CentresOverview />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/centre-detail/:centreId" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CentreDetail />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/creche/attendance" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CrecheAttendance />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/creche/funding" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CrecheFunding />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/creche/billing" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CrecheBilling />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/creche/rota" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CrecheRota />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/creche/compliance" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CrecheCompliance />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/creche/group" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CrecheGroup />
              </Suspense>
            </AppLayout>
          } />
          
          {/* BASE APPLICATION ROUTES */}
          <Route path="/home-dashboard" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <HomeDashboard />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/admin-panel" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <AdminPanel />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/marketplace" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <Marketplace />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/companies" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <Companies />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/company-detail/:companyId" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CompanyDetail />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/practices-management" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <PracticesManagement />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/practice-details" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <PracticeDetails />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/playbook-detail" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <PlaybookDetail />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/financials-dashboard" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <FinancialsDashboard />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/ai-insights" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <AIInsights />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/playbooks-management" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <PlaybooksManagement />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/integrations-management" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <IntegrationsManagement />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/smart-orchestrator" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <SmartOrchestrator />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/data-hub/profiles" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <DataHubProfiles />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/data-intelligence-suite-hub" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <DataIntelligenceSuiteHub />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/entity-resolution-queue" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <EntityResolutionQueue />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/import-drawer" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <ImportDrawer />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/payer-fee-schedules" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <PayerFeeSchedules />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/supplies-vendors" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <SuppliesVendors />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/reports" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <Reports />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/market-intelligence" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <MarketIntelligence />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/network" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <Network />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/reputation-locations" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <ReputationLocations />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/reputation-location-detail/:locationId" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <ReputationLocationDetail />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/reputation-settings" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <ReputationSettings />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/reputation-health-audit" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <ReputationHealthAudit />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/rebates-management" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <RebatesManagement />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/licenses-management" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <LicensesManagement />
              </Suspense>
            </AppLayout>
          } />
          
          <Route path="/cloud-cost-management" element={
            <AppLayout notifications={MOCK_NOTIFICATIONS}>
              <Suspense fallback={<LayoutLoadingFallback />}>
                <CloudCostManagement />
              </Suspense>
            </AppLayout>
          } />
          
          {/* 404 Route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

// Memoize Routes component to prevent unnecessary re-renders
export default React.memo(AppRoutes);